import { getApiBase } from '@papan/api-client'

/** What a share view does after binding its session (#989). */
export type BindOutcome =
  /** Bound — or left unbound because the browser won't keep the cookie (D4): carry on. */
  | { kind: 'ok' }
  /** D1: this browser is signed in as the session's own account — a session of its own. */
  | { kind: 'swap'; sessionId: string; shareToken: string; landing: { fileId: string; itemId: string } }
  /** Another browser holds the visit (409), or the session is gone (404): the front door. */
  | { kind: 'reenter'; body: { error?: unknown; shareToken?: unknown; landing?: unknown } }

/**
 * #989: bind this share session's visit to this browser before reading it. The server sets
 * the binding cookie on the first call and binds on the next, once the cookie comes back. A
 * browser that won't keep it stays unbound (D4): one retry, then carry on — never a loop. Any
 * other failure also carries on; /info then decides what the view shows.
 */
// raw (#1316): bind CREATES the next session (a swap) or decides the view re-enters — never dropped by a session end
export async function bindShareSession(sid: string, fetchImpl: typeof fetch = fetch): Promise<BindOutcome> {
  // One tab at a time (#989 review): two tabs binding at once on a browser with no cookie yet
  // would each be handed a different cookie, and a visit could end up tied to the one the jar
  // no longer holds. Serialised, a later tab sends the cookie the first one was given.
  const locks = typeof navigator !== 'undefined'
    ? (navigator as { locks?: { request: (name: string, fn: () => Promise<BindOutcome>) => Promise<BindOutcome> } }).locks
    : undefined
  if (locks?.request) {
    try {
      return await locks.request('papan-share-bind', () => bindOnce(sid, fetchImpl))
    } catch {
      return { kind: 'ok' }
    }
  }
  return bindOnce(sid, fetchImpl)
}

async function bindOnce(sid: string, fetchImpl: typeof fetch): Promise<BindOutcome> {
  try {
    for (let attempt = 0; attempt < 2; attempt++) {
      const r = await fetchImpl(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sid)}/bind`, { method: 'POST', credentials: 'include' })
      const body = (await r.json().catch(() => ({}))) as Record<string, unknown>
      if (r.status === 409 || r.status === 404) return { kind: 'reenter', body }
      if (!r.ok) return { kind: 'ok' }
      const landing = body.landing as { fileId?: unknown; itemId?: unknown } | undefined
      if (typeof body.sessionId === 'string' && typeof body.shareToken === 'string'
        && typeof landing?.fileId === 'string' && typeof landing.itemId === 'string') {
        return { kind: 'swap', sessionId: body.sessionId, shareToken: body.shareToken, landing: { fileId: landing.fileId, itemId: landing.itemId } }
      }
      if (body.retry !== true) return { kind: 'ok' }
    }
  } catch { /* the view proceeds; /info decides */ }
  return { kind: 'ok' }
}
